import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  csvDownloadURL,
  endpoints,
  type AuditEntry,
  type AuditSearchFilters,
} from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/Card";
import { fmtTimestamp } from "@/lib/format";
import { SectionTabs, SYSTEM_TABS } from "@/components/SectionTabs";

// AuditLog — v2.2.4 search page over the audit_log table. Replaces the
// "SSH + psql" workflow that the inventory flagged as a top operator
// pain point. Filters compose: leaving a field blank means "any".
// Action filter is prefix-matched so "campaign." finds every campaign
// event without typing the full action name.
export default function AuditLog() {
  // Form state (uncommitted) vs applied filters. The query only re-runs
  // when the operator clicks "搜索" so they can edit multiple fields
  // without intermediate fetches.
  const [draft, setDraft] = useState<FormState>(emptyForm());
  const [applied, setApplied] = useState<AuditSearchFilters>({});
  const [limit, setLimit] = useState(100);
  // v2.3.4 — pagination via offset (backend already supports it via Page<T>).
  // Resets to 0 when filters or limit change.
  const [page, setPage] = useState(1);

  const actors = useQuery({
    queryKey: ["audit-actors"],
    queryFn: () => endpoints.auditActors(),
    staleTime: 5 * 60_000,
  });
  const actions = useQuery({
    queryKey: ["audit-actions"],
    queryFn: () => endpoints.auditActions(),
    staleTime: 5 * 60_000,
  });
  const search = useQuery({
    queryKey: ["audit-search", applied, limit, page],
    queryFn: () => endpoints.auditSearch(applied, limit, page),
  });

  const onApply = () => {
    setApplied(formToFilters(draft));
    setPage(1);
  };
  const onReset = () => {
    setDraft(emptyForm());
    setApplied({});
    setPage(1);
  };

  const results = search.data?.data ?? [];
  const hasNext = search.data?.next_page !== undefined;

  return (
    <div className="h-full overflow-auto">
      <SectionTabs tabs={SYSTEM_TABS} />
      <div className="p-6">
      <div className="mb-4 flex items-center gap-3">
        <h1 className="text-xl font-semibold">审计日志</h1>
        <span className="text-xs text-muted-foreground">
          第 {page} 页 · 本页 {results.length} 条
        </span>
        <div className="ml-auto flex gap-2">
          <a
            href={csvDownloadURL("/api/audit/search", applied as Record<string, unknown>)}
            className="inline-flex h-8 items-center justify-center rounded-md border border-border bg-background px-3 text-xs font-medium transition-colors hover:bg-muted"
            title="导出当前筛选下的全部审计记录(最多 50000 条)"
          >
            ↓ 导出 CSV
          </a>
          <Button
            size="sm"
            variant="outline"
            disabled={page === 1 || search.isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            上一页
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!hasNext || search.isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            下一页
          </Button>
        </div>
      </div>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-sm">筛选条件</CardTitle>
          <CardDescription>留空 = 该字段不过滤;action 支持前缀匹配</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Field label="操作者 (actor)">
              <select
                value={draft.actor}
                onChange={(e) => setDraft({ ...draft, actor: e.target.value })}
                className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
              >
                <option value="">(任何)</option>
                {(actors.data ?? []).map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="动作 (action,前缀)">
              <input
                list="audit-actions-list"
                type="text"
                value={draft.action}
                onChange={(e) => setDraft({ ...draft, action: e.target.value })}
                placeholder="如 campaign. 或 sponsorship.review"
                className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
              />
              <datalist id="audit-actions-list">
                {(actions.data ?? []).map((a) => (
                  <option key={a.action} value={a.action}>
                    ({a.occurrence_count} 次)
                  </option>
                ))}
              </datalist>
            </Field>
            <Field label="实体类型 (entity_type)">
              <input
                type="text"
                value={draft.entity_type}
                onChange={(e) =>
                  setDraft({ ...draft, entity_type: e.target.value })
                }
                placeholder="campaign / channel / sponsorship..."
                className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
              />
            </Field>
            <Field label="实体 ID (entity_id)">
              <input
                type="number"
                value={draft.entity_id}
                onChange={(e) =>
                  setDraft({ ...draft, entity_id: e.target.value })
                }
                placeholder="数字 id,精确匹配"
                className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
              />
            </Field>
            <Field label="起始时间 (since)">
              <input
                type="datetime-local"
                value={draft.since}
                onChange={(e) => setDraft({ ...draft, since: e.target.value })}
                className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
              />
            </Field>
            <Field label="截止时间 (until)">
              <input
                type="datetime-local"
                value={draft.until}
                onChange={(e) => setDraft({ ...draft, until: e.target.value })}
                className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
              />
            </Field>
            <Field label="details 子串(JSONB 全文)">
              <input
                type="text"
                value={draft.details}
                onChange={(e) =>
                  setDraft({ ...draft, details: e.target.value })
                }
                placeholder="如 exchange_id 或 reason 之类的字段值"
                className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
              />
            </Field>
            <Field label="每页条数">
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
              </select>
            </Field>
          </div>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={onApply} disabled={search.isFetching}>
              {search.isFetching ? "搜索中…" : "搜索"}
            </Button>
            <Button size="sm" variant="ghost" onClick={onReset}>
              重置
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">结果</CardTitle>
        </CardHeader>
        <CardContent>
          {search.isError && (
            <p className="text-sm text-destructive">
              查询失败:{(search.error as Error).message}
            </p>
          )}
          {results.length === 0 && !search.isLoading && (
            <p className="text-sm text-muted-foreground">无匹配记录</p>
          )}
          {results.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2 pr-2">时间</th>
                  <th className="py-2 pr-2">actor</th>
                  <th className="py-2 pr-2">action</th>
                  <th className="py-2 pr-2">entity</th>
                  <th className="py-2 pr-2">details</th>
                </tr>
              </thead>
              <tbody>
                {results.map((row) => (
                  <AuditRow key={row.ID} row={row} />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}

function AuditRow({ row }: { row: AuditEntry }) {
  const [expanded, setExpanded] = useState(false);
  const details = formatDetails(row.Details);
  const truncatedDetails = details.length > 120 ? details.slice(0, 120) + "…" : details;
  return (
    <tr className="border-b border-border last:border-0 align-top">
      <td className="py-1.5 pr-2 whitespace-nowrap text-muted-foreground">
        {fmtTimestamp(row.Timestamp)}
      </td>
      <td className="py-1.5 pr-2 whitespace-nowrap">
        {row.Actor ? (
          <Badge variant="secondary" className="text-[10px]">
            {row.Actor}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="py-1.5 pr-2 font-mono whitespace-nowrap">{row.Action}</td>
      <td className="py-1.5 pr-2 whitespace-nowrap text-muted-foreground">
        {row.EntityType
          ? `${row.EntityType}${row.EntityID ? "#" + row.EntityID : ""}`
          : "—"}
      </td>
      <td className="py-1.5 pr-2">
        {details ? (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-left font-mono text-[10px] text-muted-foreground hover:text-foreground"
          >
            {expanded ? details : truncatedDetails}
          </button>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  );
}

// -----------------------------------------------------------------------------
// helpers
// -----------------------------------------------------------------------------

interface FormState {
  actor: string;
  action: string;
  entity_type: string;
  entity_id: string;
  since: string; // datetime-local format
  until: string;
  details: string;
}
const emptyForm = (): FormState => ({
  actor: "",
  action: "",
  entity_type: "",
  entity_id: "",
  since: "",
  until: "",
  details: "",
});

function formToFilters(f: FormState): AuditSearchFilters {
  const out: AuditSearchFilters = {};
  if (f.actor.trim()) out.actor = f.actor.trim();
  if (f.action.trim()) out.action = f.action.trim();
  if (f.entity_type.trim()) out.entity_type = f.entity_type.trim();
  if (f.entity_id.trim()) {
    const n = parseInt(f.entity_id, 10);
    if (!Number.isNaN(n)) out.entity_id = n;
  }
  // datetime-local is local TZ; the backend expects RFC3339. Browser
  // gives YYYY-MM-DDTHH:mm; we attach :00 + the local offset.
  if (f.since.trim()) out.since = localToRFC3339(f.since);
  if (f.until.trim()) out.until = localToRFC3339(f.until);
  if (f.details.trim()) out.details = f.details.trim();
  return out;
}

function localToRFC3339(local: string): string {
  // local: "2026-05-15T08:30"
  // Convert via Date to add local TZ offset, then toISOString → UTC
  // (Z suffix). Backend's time.Parse(RFC3339) accepts both.
  try {
    return new Date(local).toISOString();
  } catch {
    return "";
  }
}

function formatDetails(d: unknown): string {
  if (d === null || d === undefined) return "";
  // Backend serializes details as raw JSON (object/array/scalar) via
  // json.RawMessage; pretty-print compactly so the inline row stays
  // readable but the full object is one click away (expand handler).
  try {
    return JSON.stringify(d);
  } catch {
    return String(d);
  }
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
